/**
 * Knitster LDN - Site content registry
 *
 * Every piece of editable text and every editable image on the website is
 * declared here. The admin "Page Content" editor builds its entire UI from this
 * schema, and the pages read their copy through `usePageContent()`.
 *
 * To make something new editable:
 *   1. Add a field to the relevant group below (with its current value as `default`).
 *   2. Use `t('groupKey.fieldKey')` in the page component.
 * The admin editor picks it up automatically - no admin code to change.
 *
 * Field types:
 *   text     - single line
 *   textarea - multi-line paragraph
 *   lines    - list of short strings, one per line (bullets, paragraphs, addresses)
 *   image    - image path, editable via upload / library / manual path
 *   url      - link target
 *   list     - repeatable group of fields, described by `itemFields`
 */

export const CONTENT_SCHEMA = [
    // ---------------------------------------------------------------- GLOBAL
    {
        key: 'global',
        label: 'Site-wide',
        description: 'Logo, navigation labels, footer and shared contact details.',
        path: '/',
        groups: [
            {
                key: 'header',
                label: 'Header & navigation',
                fields: [
                    { key: 'logo', label: 'Logo', type: 'image', default: '/images/logo.png' },
                    { key: 'logoAlt', label: 'Logo alt text', type: 'text', default: 'Knitster LDN' },
                    { key: 'navDesign', label: 'Nav: Design & Development', type: 'text', default: 'Design & Development' },
                    { key: 'navPortfolio', label: 'Nav: Portfolio', type: 'text', default: 'Portfolio' },
                    { key: 'navMadeInLondon', label: 'Nav: Made In London', type: 'text', default: 'Made In London' },
                    { key: 'navOverseas', label: 'Nav: Overseas Production', type: 'text', default: 'Overseas Production' },
                    { key: 'navValues', label: 'Nav: Our Values', type: 'text', default: 'Our Values' },
                    { key: 'navContact', label: 'Nav: Contact', type: 'text', default: 'Contact' },
                    { key: 'navCareers', label: 'Nav: Careers', type: 'text', default: 'Careers' }
                ]
            },
            {
                key: 'contact',
                label: 'Shared contact details',
                description: 'Used by the "Email Us" buttons across the site and by the Contact page.',
                fields: [
                    { key: 'email', label: 'Main email address', type: 'text', default: 'knit@knitster-ldn.com' },
                    { key: 'phone', label: 'Main phone number', type: 'text', default: '+44 (0) 208 988 1768' },
                    { key: 'phoneLink', label: 'Main phone number (dial format)', type: 'text', default: '+442089881768' }
                ]
            },
            {
                key: 'footer',
                label: 'Footer',
                fields: [
                    { key: 'col1Title', label: 'Column 1 heading', type: 'text', default: 'SUSTAINABLE YARNS & PRACTICES' },
                    { key: 'col1LinkText', label: 'Column 1 link text', type: 'text', default: 'Proud to be part of the British wool revival' },
                    { key: 'col1LinkUrl', label: 'Column 1 link URL', type: 'url', default: 'https://www.greatbritishwoolrevival.co.uk/' },
                    { key: 'col2Title', label: 'Column 2 heading', type: 'text', default: 'MADE IN ENGLAND' },
                    { key: 'col2Text', label: 'Column 2 text', type: 'textarea', default: 'We ship globally from our UK based factory' },
                    { key: 'col3Title', label: 'Column 3 heading', type: 'text', default: 'ETHICAL PRODUCTION' },
                    { key: 'col3Link1', label: 'Column 3 link 1 text', type: 'text', default: 'Read our Modern Slavery statement' },
                    { key: 'col3Link2', label: 'Column 3 link 2 text', type: 'text', default: 'Read our Environmental Policy' },
                    { key: 'facebookUrl', label: 'Facebook URL', type: 'url', default: 'https://www.facebook.com/KnitsterLDN/' },
                    { key: 'instagramUrl', label: 'Instagram URL', type: 'url', default: 'https://www.instagram.com/knitster_ldn/?hl=en' },
                    { key: 'copyrightName', label: 'Copyright name', type: 'text', default: 'Knitster LDN. All rights reserved.' },
                    { key: 'credit', label: 'Credit line', type: 'text', default: 'A Website CraftedByJDP' }
                ]
            }
        ]
    },

    // ------------------------------------------------------------------ HOME
    {
        key: 'home',
        label: 'Home',
        path: '/',
        groups: [
            {
                key: 'hero',
                label: 'Hero',
                fields: [
                    { key: 'image', label: 'Background image', type: 'image', default: '/images/INTRO PAGE.jpg' },
                    { key: 'imageAlt', label: 'Background image alt text', type: 'text', default: 'Knitster LDN luxury knitwear' },
                    { key: 'prompt', label: 'Prompt under the logo', type: 'text', default: 'Click for more information' },
                    { key: 'tagline', label: 'Tagline', type: 'text', default: 'Luxury Knitwear Manufacturing' },
                    { key: 'established', label: 'Established line', type: 'text', default: 'Established 2013 \u2022 London, UK' }
                ]
            },
            {
                key: 'intro',
                label: 'Intro panel (shown when the logo is clicked)',
                fields: [
                    {
                        key: 'paragraphs',
                        label: 'Paragraphs (one per line)',
                        type: 'lines',
                        default: [
                            "Knitster LDN is a London based design studio and manufacturer specialising in the production of luxury knitwear with an emphasis on ethical production, quality and innovation. We produce men's and women's fully fashioned sweaters and accessories on behalf of a varied portfolio of clients.",
                            'We also consult on and produce bespoke pieces for creative advertising, merchandise and film projects. The jewel in our crown is our London based factory complete with state-of-the-art machinery and skilled craftspeople.',
                            'In 2025 we designed and launched Knitstore LDN, our own capsule collection of high-quality staple sweaters knitted and finished in our London factory from British wool. As part of our commitment to sustainability, the accessories in the range are crafted from dead-stock British Wool.'
                        ]
                    },
                    { key: 'stockistBefore', label: 'Stockist line - text before the link', type: 'textarea', default: 'The Knitstore LDN collection is available to purchase at our stockist' },
                    { key: 'stockistLinkText', label: 'Stockist link text', type: 'text', default: 'Straw London' },
                    { key: 'stockistLinkUrl', label: 'Stockist link URL', type: 'url', default: 'https://www.strawlondon.co.uk/clothing' },
                    { key: 'stockistAfter', label: 'Stockist line - text after the link', type: 'textarea', default: 'and by contacting our Knitstore LDN team directly.' },
                    { key: 'emailText', label: 'Email link text', type: 'text', default: 'knit@knitster-ldn.com' },
                    { key: 'emailAddress', label: 'Email link address', type: 'text', default: 'knit@knitster-ldn.com' },
                    { key: 'instagramText', label: 'Instagram link text', type: 'text', default: '@knitstore_ldn' },
                    { key: 'instagramUrl', label: 'Instagram link URL', type: 'url', default: 'https://www.instagram.com/knitstore_ldn/' },
                    { key: 'closePrompt', label: 'Close prompt', type: 'text', default: 'Click to close' }
                ]
            }
        ]
    },

    // ------------------------------------------------- DESIGN & DEVELOPMENT
    {
        key: 'designDevelopment',
        label: 'Design & Development',
        path: '/design-development',
        groups: [
            {
                key: 'hero',
                label: 'Hero',
                fields: [
                    { key: 'image', label: 'Background image', type: 'image', default: '/images/Design-and-Development.jpg' },
                    { key: 'title', label: 'Page title', type: 'text', default: 'DESIGN & DEVELOPMENT' }
                ]
            },
            {
                key: 'intro',
                label: 'Introduction',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'COMPREHENSIVE DESIGN SERVICES' },
                    {
                        key: 'paragraphs',
                        label: 'Paragraphs (one per line)',
                        type: 'lines',
                        default: [
                            'From initial concept to final sample, our design and development team works closely with designers to translate creative vision into technically viable, beautifully crafted knitwear.',
                            'We offer full-service design consultation, sampling, prototyping, and technical development, ensuring every piece meets both aesthetic and production requirements.'
                        ]
                    }
                ]
            },
            {
                key: 'services',
                label: 'Our services',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'OUR SERVICES' },
                    {
                        key: 'cards',
                        label: 'Service cards',
                        type: 'list',
                        itemLabel: 'Service',
                        itemFields: [
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' },
                            { key: 'bullets', label: 'Bullet points (one per line)', type: 'lines' }
                        ],
                        default: [
                            {
                                title: 'DESIGN CONSULTATION',
                                body: 'Our experienced team works closely with designers to translate creative vision into technically viable knitwear. We offer expertise in yarn selection, stitch development, and garment construction.',
                                bullets: [
                                    'Concept development and feasibility assessment',
                                    'Technical advice and material recommendations',
                                    'Cost analysis and production planning',
                                    'Trend forecasting and market insights'
                                ]
                            },
                            {
                                title: 'SAMPLING & PROTOTYPING',
                                body: 'We develop comprehensive swatches and prototypes that showcase our technical capabilities and allow designers to refine their designs before production.',
                                bullets: [
                                    'Stitch and texture development',
                                    'Color work and pattern creation',
                                    'Size grading and fit assessment',
                                    'Multiple prototype iterations'
                                ]
                            },
                            {
                                title: 'TECHNICAL DEVELOPMENT',
                                body: 'Complete technical development services from initial sketches through to production-ready specifications, ensuring every detail is perfected.',
                                bullets: [
                                    'Tech pack creation and specification sheets',
                                    'Digital knit programming',
                                    'Material sourcing and trim selection',
                                    'Quality control standards development'
                                ]
                            },
                            {
                                title: 'FULL PRODUCTION MANAGEMENT',
                                body: 'End-to-end project management from sample approval through to final delivery, with transparent communication at every stage.',
                                bullets: [
                                    'Production timeline planning',
                                    'Quality assurance throughout manufacturing',
                                    'Progress updates and milestone reporting',
                                    'Final inspection and delivery coordination'
                                ]
                            }
                        ]
                    }
                ]
            },
            {
                key: 'process',
                label: 'Development process',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'OUR DEVELOPMENT PROCESS' },
                    {
                        key: 'steps',
                        label: 'Process steps',
                        type: 'list',
                        itemLabel: 'Step',
                        itemFields: [
                            { key: 'image', label: 'Icon', type: 'image' },
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            {
                                image: '/images/initial-consultation.png',
                                title: 'INITIAL CONSULTATION',
                                body: "Meet with directors Aysen Bayram and Emma McClelland to discuss your vision, timeline, and budget. We'll assess feasibility and provide initial guidance on materials and construction methods."
                            },
                            {
                                image: '/images/concept-dev.png',
                                title: 'CONCEPT DEVELOPMENT',
                                body: 'Our product development team creates initial tech packs, sources yarns and materials, and develops a comprehensive sampling strategy aligned with your creative vision.'
                            },
                            {
                                image: '/images/sampling+iteration.png',
                                title: 'SAMPLING & ITERATION',
                                body: 'Our programmer creates digital knit programs and produces initial samples. We work through multiple iterations, refining fit, drape, and details until the design is perfected.'
                            },
                            {
                                image: '/images/production+approval.png',
                                title: 'PRODUCTION APPROVAL',
                                body: 'Final sample approval, production timeline confirmation, and detailed quality control standards documentation ensure a smooth transition from development to manufacturing.'
                            },
                            {
                                image: '/images/manfactoring+delivery.png',
                                title: 'MANUFACTURING & DELIVERY',
                                body: 'Full production begins with ongoing quality control, progress updates, and final inspection before delivery. We maintain close communication throughout the entire process.'
                            }
                        ]
                    }
                ]
            },
            {
                key: 'cta',
                label: 'Call to action',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'Ready to Develop Your Collection?' },
                    { key: 'body', label: 'Text', type: 'textarea', default: 'Contact us to discuss your design needs and discover how our experienced team can bring your knitwear vision to life.' },
                    { key: 'buttonText', label: 'Button text', type: 'text', default: 'Email Us' }
                ]
            }
        ]
    },

    // ------------------------------------------------------------- PORTFOLIO
    {
        key: 'portfolio',
        label: 'Portfolio',
        path: '/portfolio',
        description: 'The projects themselves are managed in the Portfolio Manager.',
        groups: [
            {
                key: 'hero',
                label: 'Hero',
                fields: [
                    { key: 'image', label: 'Background image', type: 'image', default: '/images/Portfolio.jpg' },
                    { key: 'title', label: 'Page title', type: 'text', default: 'PORTFOLIO' }
                ]
            },
            {
                key: 'intro',
                label: 'Introduction',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'OUR WORK' },
                    { key: 'body', label: 'Text', type: 'textarea', default: "We've had the privilege of collaborating with the world's most prestigious fashion houses and emerging design talents. Each project reflects our commitment to quality, innovation, and sustainable manufacturing practices." },
                    { key: 'categoryReadyToWear', label: 'Filter label: Ready to wear', type: 'text', default: 'READY TO WEAR' },
                    { key: 'categoryRunway', label: 'Filter label: Runway', type: 'text', default: 'RUNWAY' },
                    { key: 'categoryMerchandise', label: 'Filter label: Merchandise', type: 'text', default: 'MERCHANDISE' },
                    { key: 'viewProjectText', label: 'Hover label on a project', type: 'text', default: 'View Project' },
                    { key: 'emptyText', label: 'Message when a category is empty', type: 'text', default: 'Portfolio projects coming soon.' },
                    { key: 'loadingText', label: 'Loading message', type: 'text', default: 'Loading portfolio...' }
                ]
            },
            {
                key: 'cta',
                label: 'Call to action',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'Interested in Working Together?' },
                    { key: 'body', label: 'Text', type: 'textarea', default: 'Contact us to discuss your knitwear requirements and discover how we can bring your designs to life with exceptional quality and craftsmanship.' },
                    { key: 'buttonText', label: 'Button text', type: 'text', default: 'Email Us' }
                ]
            }
        ]
    },

    // -------------------------------------------------------- MADE IN LONDON
    {
        key: 'madeInLondon',
        label: 'Made In London',
        path: '/made-in-london',
        groups: [
            {
                key: 'hero',
                label: 'Hero',
                fields: [
                    { key: 'image', label: 'Background image', type: 'image', default: '/images/Our-Values.jpg' },
                    { key: 'title', label: 'Page title', type: 'text', default: 'MADE IN LONDON' }
                ]
            },
            {
                key: 'intro',
                label: 'Introduction',
                fields: [
                    {
                        key: 'paragraphs',
                        label: 'Paragraphs (one per line)',
                        type: 'lines',
                        default: [
                            'Our London-based facility is the heart of Knitster LDN. Here, our dedicated team of 14 skilled craftspeople produce up to 20,000 pieces of luxury knitwear annually, managing every stage from initial concept to finished garment.',
                            'We specialize in mid to heavy gauge, fully fashioned knitwear, hand-finished with precision. Using both Shima and Stoll technology, we combine traditional craftsmanship with modern efficiency.'
                        ]
                    }
                ]
            },
            {
                key: 'capabilities',
                label: 'Production capabilities',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'PRODUCTION CAPABILITIES' },
                    {
                        key: 'stats',
                        label: 'Stat cards',
                        type: 'list',
                        itemLabel: 'Stat',
                        itemFields: [
                            { key: 'value', label: 'Big number', type: 'text' },
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            { value: '20,000', title: 'Annual Capacity', body: 'Knitwear pieces produced annually at our London facility' },
                            { value: '14', title: 'Team Members', body: 'Dedicated staff providing exceptional craftsmanship' },
                            { value: '50', title: 'MOQ', body: 'Minimum order quantity per style' },
                            { value: '2013', title: 'Established', body: 'Over a decade of luxury knitwear production' }
                        ]
                    }
                ]
            },
            {
                key: 'process',
                label: 'From concept to completion',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'FROM CONCEPT TO COMPLETION' },
                    {
                        key: 'steps',
                        label: 'Process steps',
                        type: 'list',
                        itemLabel: 'Step',
                        itemFields: [
                            { key: 'image', label: 'Icon', type: 'image' },
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            {
                                image: '/images/Briefing-Planning.png',
                                title: 'BRIEFING & PLANNING',
                                body: 'Initial consultation with directors Aysen Bayram & Emma McClelland for concept development and sample strategy.'
                            },
                            {
                                image: '/images/Development-Programming.png',
                                title: 'DEVELOPMENT & PROGRAMMING',
                                body: 'Our programmer creates digital knit programs while our product development team sources materials and develops tech packs.'
                            },
                            {
                                image: '/images/Production.png',
                                title: 'PRODUCTION',
                                body: 'Our skilled technicians, linkers, and craftspeople execute bulk production using approved samples and specifications.'
                            },
                            {
                                image: '/images/Finishing-QC.png',
                                title: 'FINISHING & QC',
                                body: 'Final quality control checks, hand-finishing, and packing following our strict quality manual standards.'
                            }
                        ]
                    }
                ]
            },
            {
                key: 'machinery',
                label: 'Machinery & equipment',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'MACHINERY & EQUIPMENT' },
                    {
                        key: 'knitting',
                        label: 'Knitting machines',
                        type: 'list',
                        itemLabel: 'Machine',
                        itemFields: [
                            { key: 'title', label: 'Name', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            { title: 'Shima SES122 - 12gg', body: 'Fine gauge flat bed knitting machine for delicate, detailed knitwear production.' },
                            { title: 'Shima SIG123 - 7gg/5gg Multi', body: 'Multi-gauge capability for versatile mid-weight knitwear production.' },
                            { title: 'Shima NSSR122 - 7gg/5gg Multi', body: 'Advanced multi-gauge machine for complex structures and patterns.' },
                            { title: 'Shima SES122 - 3gg', body: 'Heavy gauge machine for chunky knits and bold textures.' },
                            { title: 'Stoll CMS530HP - 5gg', body: 'High-performance Stoll machine for precision mid-weight production.' },
                            { title: 'Dubied - 12gg', body: 'Hand frame knitting for traditional craftsmanship and samples.' }
                        ]
                    },
                    { key: 'linkingTitle', label: 'Linking machines heading', type: 'text', default: 'LINKING MACHINES' },
                    {
                        key: 'linking',
                        label: 'Linking machines',
                        type: 'list',
                        itemLabel: 'Machine',
                        itemFields: [
                            { key: 'title', label: 'Name', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            { title: 'Complett - 12gg', body: 'Fine gauge linking for seamless finishing.' },
                            { title: 'Complett - 7gg', body: 'Medium gauge linking for versatile applications.' },
                            { title: 'Complett - 5gg', body: 'Mid-heavy gauge linking for structured pieces.' },
                            { title: 'Exacta - 3gg', body: 'Heavy gauge linking for chunky knitwear.' }
                        ]
                    },
                    { key: 'equipmentTitle', label: 'Equipment & software heading', type: 'text', default: 'EQUIPMENT & SOFTWARE' },
                    {
                        key: 'equipment',
                        label: 'Equipment & software',
                        type: 'list',
                        itemLabel: 'Item',
                        itemFields: [
                            { key: 'title', label: 'Name', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            { title: 'Industrial Washer & Dryer', body: 'Electrolux industrial equipment for professional finishing.' },
                            { title: 'Corghi Cone Winder', body: 'Precision yarn winding for production preparation.' },
                            { title: 'Industrial Iron & Press', body: 'Professional pressing equipment for perfect finishing.' },
                            { title: 'Sewing & Overlocker', body: 'Industrial machines for trim attachment and finishing.' },
                            { title: 'Shima Apex 3', body: 'Advanced programming system for Shima machines.' },
                            { title: 'Stoll M1plus', body: 'Professional programming system for Stoll equipment.' }
                        ]
                    }
                ]
            },
            {
                key: 'location',
                label: 'Visit our facility',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'Visit Our London Facility' },
                    {
                        key: 'address',
                        label: 'Address (one line per row)',
                        type: 'lines',
                        default: ['Unit 5, Leyton Studios', '15 Argall Avenue', 'London, E10 7QE, United Kingdom']
                    },
                    { key: 'body', label: 'Text', type: 'textarea', default: 'Contact us to schedule a visit and see our production process firsthand.' },
                    { key: 'buttonText', label: 'Button text', type: 'text', default: 'Email Us' }
                ]
            }
        ]
    },

    // --------------------------------------------------- OVERSEAS PRODUCTION
    {
        key: 'overseasProduction',
        label: 'Overseas Production',
        path: '/overseas-production',
        groups: [
            {
                key: 'hero',
                label: 'Hero',
                fields: [
                    { key: 'image', label: 'Background image', type: 'image', default: '/images/Overseas-Production.jpeg' },
                    { key: 'title', label: 'Page title', type: 'text', default: 'OVERSEAS PRODUCTION' }
                ]
            },
            {
                key: 'intro',
                label: 'Introduction',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'SCALABLE PRODUCTION SOLUTIONS' },
                    {
                        key: 'paragraphs',
                        label: 'Paragraphs (one per line)',
                        type: 'lines',
                        default: [
                            'In addition to our London facility, we have established trusted partnerships with high-quality knitwear factories in Turkey, Romania, Bulgaria, China, and Italy. These partnerships allow us to offer scalable production solutions while maintaining our exacting quality standards.',
                            "Our overseas partners are carefully selected for their technical capabilities, ethical practices, and commitment to quality. We closely monitor all our overseas production runs to ensure quality aligns with both the client's specification and our exacting standards.",
                            'When producing overseas we use local yarns where appropriate with the intention of simplifying the supply chain and minimising the footprint of the end product.',
                            'Whether you need 100 pieces or 10,000, we can recommend the most appropriate production solution to meet your requirements and timeline.'
                        ]
                    }
                ]
            },
            {
                key: 'reasons',
                label: 'When to consider overseas production',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'WHEN TO CONSIDER OVERSEAS PRODUCTION' },
                    {
                        key: 'cards',
                        label: 'Cards',
                        type: 'list',
                        itemLabel: 'Reason',
                        itemFields: [
                            { key: 'image', label: 'Icon', type: 'image' },
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            {
                                image: '/images/Larger-Volumes.png',
                                title: 'LARGER VOLUMES',
                                body: 'For orders exceeding our 20,000-piece annual London capacity, our overseas partners provide the infrastructure to scale production efficiently.'
                            },
                            {
                                image: '/images/Faster-Turn-Around.png',
                                title: 'FASTER TURNAROUND',
                                body: 'Overseas facilities with dedicated production lines can often accommodate tighter deadlines for bulk orders.'
                            },
                            {
                                image: '/images/Cost-Optimisation.png',
                                title: 'COST OPTIMISATION',
                                body: 'Competitive pricing on larger production runs while maintaining quality standards and ethical manufacturing practices.'
                            }
                        ]
                    }
                ]
            },
            {
                key: 'partners',
                label: 'Partner locations',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'OUR PARTNER LOCATIONS' },
                    {
                        key: 'locations',
                        label: 'Locations (one per line)',
                        type: 'lines',
                        default: ['Turkey', 'Romania', 'Bulgaria', 'China', 'Italy']
                    }
                ]
            },
            {
                key: 'quality',
                label: 'Quality assurance',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'QUALITY ASSURANCE' },
                    { key: 'intro', label: 'Intro text', type: 'textarea', default: 'Maintaining quality standards across all production locations is our top priority. We implement rigorous quality control processes and regular factory audits to ensure every piece meets our exacting standards.' },
                    { key: 'qcTitle', label: 'Left card heading', type: 'text', default: 'Quality Control Process' },
                    {
                        key: 'qcItems',
                        label: 'Left card bullets (one per line)',
                        type: 'lines',
                        default: [
                            'Pre-production sample approval',
                            'In-line inspection during production',
                            'Final inspection before shipment',
                            'Random sampling and testing',
                            'Detailed inspection reports'
                        ]
                    },
                    { key: 'auditTitle', label: 'Right card heading', type: 'text', default: 'Factory Audits & Standards' },
                    {
                        key: 'auditItems',
                        label: 'Right card bullets (one per line)',
                        type: 'lines',
                        default: [
                            'Regular factory audits and assessments',
                            'Ethical manufacturing certifications',
                            'Worker welfare and safety standards',
                            'Environmental compliance verification',
                            'Transparent supply chain documentation'
                        ]
                    }
                ]
            },
            {
                key: 'howWeWork',
                label: 'How we manage overseas production',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'HOW WE MANAGE OVERSEAS PRODUCTION' },
                    {
                        key: 'steps',
                        label: 'Numbered steps',
                        type: 'list',
                        itemLabel: 'Step',
                        itemFields: [
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            { title: 'Initial Assessment', body: 'We assess your requirements and recommend the most appropriate production partner based on quantity, complexity, and timeline considerations.' },
                            { title: 'Sample Development', body: 'All initial samples are developed in our London facility to ensure technical accuracy before transitioning to overseas production.' },
                            { title: 'Production Management', body: 'Our team maintains close communication with factory partners, conducting regular inspections and providing technical support throughout production.' },
                            { title: 'Quality Verification', body: 'Final quality control inspection is conducted before shipment, with detailed reports and any necessary corrections handled promptly.' },
                            { title: 'Logistics & Delivery', body: 'We coordinate all shipping and customs documentation, ensuring timely delivery to your specified location with full transparency on timing.' }
                        ]
                    }
                ]
            },
            {
                key: 'cta',
                label: 'Call to action',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'Explore Overseas Production Options' },
                    { key: 'body', label: 'Text', type: 'textarea', default: "Contact us to discuss your production needs. We'll recommend the best manufacturing solution for your requirements, whether in London or through our trusted overseas partners." },
                    { key: 'buttonText', label: 'Button text', type: 'text', default: 'DISCUSS PRODUCTION OPTIONS' }
                ]
            }
        ]
    },

    // ------------------------------------------------------------ OUR VALUES
    {
        key: 'ourValues',
        label: 'Our Values',
        path: '/our-values',
        groups: [
            {
                key: 'hero',
                label: 'Hero',
                fields: [
                    { key: 'image', label: 'Background image', type: 'image', default: '/images/Made-in-London.jpg' },
                    { key: 'title', label: 'Page title', type: 'text', default: 'OUR VALUES' }
                ]
            },
            {
                key: 'mission',
                label: 'Our mission',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'OUR MISSION' },
                    {
                        key: 'paragraphs',
                        label: 'Paragraphs (one per line)',
                        type: 'lines',
                        default: [
                            'Established in 2013, Knitster LDN was founded with a clear mission; to produce exceptional quality knitwear with a focus on craftsmanship, innovation and sustainability.',
                            'We believe in creating beautiful, well-made garments that stand the test of time, thus challenging the fast fashion mindset which has dominated all levels of our industry in recent years.',
                            'Our goal is to be at the forefront of knitwear design and manufacture while maintaining the highest standards of ethical and responsibility in our own factory and at our overseas partner factories.'
                        ]
                    }
                ]
            },
            {
                key: 'values',
                label: 'What we stand for',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'WHAT WE STAND FOR' },
                    {
                        key: 'cards',
                        label: 'Value cards',
                        type: 'list',
                        itemLabel: 'Value',
                        itemFields: [
                            { key: 'image', label: 'Icon', type: 'image' },
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            {
                                image: '/images/British-Manufacture.png',
                                title: 'BRITISH MANUFACTURING',
                                body: "We're proud to be part of the British supply chain, supporting local industry and championing UK wool while reducing our carbon footprint."
                            },
                            {
                                image: '/images/Precision+Quality.png',
                                title: 'PRECISION & QUALITY',
                                body: 'Using both Shima and Stoll technology, we combine precision manufacturing with exceptional craftsmanship to deliver consistent, high-quality results.'
                            },
                            {
                                image: '/images/Sustainable-Practices.png',
                                title: 'SUSTAINABLE PRACTICES',
                                body: 'From fiber selection to finished product, we prioritize natural, recyclable materials and ethical production methods that benefit people and planet.'
                            }
                        ]
                    }
                ]
            },
            {
                key: 'founders',
                label: 'Meet the founders',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'MEET THE FOUNDERS' },
                    {
                        key: 'people',
                        label: 'Founders',
                        type: 'list',
                        itemLabel: 'Founder',
                        itemFields: [
                            { key: 'image', label: 'Portrait', type: 'image' },
                            { key: 'name', label: 'Name', type: 'text' },
                            { key: 'role', label: 'Role', type: 'text' },
                            { key: 'bio', label: 'Biography', type: 'textarea' }
                        ],
                        default: [
                            {
                                image: '/images/aysen-portrait.jpg',
                                name: 'Aysen Bayram',
                                role: 'Co-Founder & Creative Director',
                                bio: "Born in London's East End to Turkish Cypriot parents, Aysen draws inspiration from her native city and a family heritage of craftsmanship. Her parents owned a sewing factory in London in the 1980s, and she comes from two generations of seamstresses; her grandmother wove her own silk. After studying Fashion with Textiles at Ravensbourne and working for Ally Capellino and Tommy Hilfiger, she established Knitster LDN in 2013. A true appreciation of craftsmanship, coupled with an instinctive eye for colour, characterises Aysen's work."
                            },
                            {
                                image: '/images/emma-portrait.jpg',
                                name: 'Emma McClelland',
                                role: 'Co-Founder & Design Director',
                                bio: "Dublin-born Emma's passion for textiles began watching her mother craft traditional Aran sweaters. After studying fashion design in Dublin and Milan, she worked for 8 years as an in-house knitwear designer at Marks & Spencer. Emma joined forces with Aysen to transform Knitster LDN into the industry-leading manufacturer it is today."
                            }
                        ]
                    }
                ]
            }
        ]
    },

    // ------------------------------------------------------------ KNIT STORE
    {
        key: 'knitStore',
        label: 'Knit Store',
        path: '/knit-store',
        groups: [
            {
                key: 'hero',
                label: 'Hero',
                fields: [
                    { key: 'image', label: 'Background image', type: 'image', default: '/images/Yarn-Shop.jpg' },
                    { key: 'title', label: 'Page title', type: 'text', default: 'KNIT STORE' }
                ]
            },
            {
                key: 'intro',
                label: 'Introduction',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'PREMIUM YARNS & FIBERS' },
                    {
                        key: 'paragraphs',
                        label: 'Paragraphs (one per line)',
                        type: 'lines',
                        default: [
                            'As wool specialists, we work closely with UK spinners and champion British wool, celebrating local skills and natural fibers in every garment. Our yarn selection reflects our commitment to quality, sustainability, and traceability.',
                            'We source a carefully curated range of natural, sustainable, and innovative yarns from trusted suppliers across the UK and Europe. Every yarn in our collection is chosen for its quality, performance, and environmental credentials.'
                        ]
                    }
                ]
            },
            {
                key: 'collection',
                label: 'Yarn collection',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'OUR YARN COLLECTION' },
                    {
                        key: 'cards',
                        label: 'Yarn categories',
                        type: 'list',
                        itemLabel: 'Category',
                        itemFields: [
                            { key: 'emoji', label: 'Emoji', type: 'text' },
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' },
                            { key: 'bullets', label: 'Bullet points (one per line)', type: 'lines' }
                        ],
                        default: [
                            {
                                emoji: '🐑',
                                title: 'BRITISH WOOL',
                                body: 'Our specialty and passion. We champion British wool from local farms, celebrating native breeds and supporting UK agriculture.',
                                bullets: ['Blue-faced Leicester', 'Shetland', 'Romney Marsh', 'Welsh Mountain', 'Wensleydale']
                            },
                            {
                                emoji: '🌿',
                                title: 'ORGANIC & NATURAL',
                                body: 'Certified organic and naturally processed fibers that are gentle on the environment and luxurious to wear.',
                                bullets: ['Organic Merino Wool', 'Organic Cotton', 'Alpaca', 'Cashmere', 'Mohair']
                            },
                            {
                                emoji: '♻️',
                                title: 'RECYCLED & SUSTAINABLE',
                                body: 'Innovative recycled yarns that give new life to post-consumer and post-industrial textile waste without compromising quality.',
                                bullets: ['Recycled Wool', 'Recycled Cotton', 'Recycled Cashmere', 'Recycled Polyester', 'Econyl (Recycled Nylon)']
                            }
                        ]
                    }
                ]
            },
            {
                key: 'spinners',
                label: 'UK spinner partnerships',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'UK SPINNER PARTNERSHIPS' },
                    { key: 'intro', label: 'Intro text', type: 'textarea', default: 'We maintain close relationships with British spinning mills, ensuring complete traceability and supporting the UK textile industry. These partnerships allow us to offer custom yarn development and guarantee the provenance of every fiber.' },
                    { key: 'specialtiesLabel', label: 'Specialties label', type: 'text', default: 'Specialties:' },
                    {
                        key: 'cards',
                        label: 'Spinner partners',
                        type: 'list',
                        itemLabel: 'Partner',
                        itemFields: [
                            { key: 'title', label: 'Name', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' },
                            { key: 'specialties', label: 'Specialties', type: 'text' }
                        ],
                        default: [
                            {
                                title: 'Yorkshire Mills',
                                body: 'Partnering with historic Yorkshire woolen mills for traditional British wool yarns, spun using time-honored techniques combined with modern quality standards.',
                                specialties: 'Worsted wools, British breed blends, heavy gauge yarns'
                            },
                            {
                                title: 'Scottish Spinners',
                                body: 'Working with Scottish spinning mills renowned for their expertise in fine gauge yarns and luxury fibers like cashmere and mohair blends.',
                                specialties: 'Fine gauge yarns, luxury blends, Shetland wool'
                            },
                            {
                                title: 'Welsh Producers',
                                body: 'Collaborating with Welsh wool producers for distinctive mountain breed yarns with unique characteristics and exceptional durability.',
                                specialties: 'Welsh Mountain wool, hardy breeds, textured yarns'
                            },
                            {
                                title: 'Organic Specialists',
                                body: 'Partnerships with certified organic spinners who process wool and cotton without harmful chemicals, maintaining the highest environmental standards.',
                                specialties: 'GOTS certified yarns, organic cotton, natural dyes'
                            }
                        ]
                    }
                ]
            },
            {
                key: 'gauge',
                label: 'Gauge range',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'GAUGE RANGE' },
                    { key: 'intro', label: 'Intro text', type: 'textarea', default: 'We specialize in mid to heavy gauge knitwear, with yarn selections perfectly suited to our machinery capabilities and expertise.' },
                    {
                        key: 'items',
                        label: 'Gauges',
                        type: 'list',
                        itemLabel: 'Gauge',
                        itemFields: [
                            { key: 'gauge', label: 'Gauge', type: 'text' },
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            { gauge: '3gg', title: 'Heavy Gauge', body: 'Chunky knits, bold textures, statement pieces' },
                            { gauge: '5gg', title: 'Mid-Heavy', body: 'Structured knitwear, outerwear, classic pieces' },
                            { gauge: '7gg', title: 'Medium Gauge', body: 'Versatile knitwear, layering pieces, all-season' },
                            { gauge: '12gg', title: 'Fine Gauge', body: 'Refined knitwear, detailed stitches, elegant finish' }
                        ]
                    }
                ]
            },
            {
                key: 'certifications',
                label: 'Yarn certifications',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'YARN CERTIFICATIONS' },
                    { key: 'intro', label: 'Intro text', type: 'textarea', default: 'We prioritize yarns with recognized sustainability and quality certifications, ensuring transparency and ethical production throughout our supply chain.' },
                    {
                        key: 'items',
                        label: 'Certifications',
                        type: 'list',
                        itemLabel: 'Certification',
                        itemFields: [
                            { key: 'emoji', label: 'Emoji', type: 'text' },
                            { key: 'title', label: 'Name', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            { emoji: '🌱', title: 'GOTS', body: 'Global Organic Textile Standard for organic fibers' },
                            { emoji: '✓', title: 'OEKO-TEX', body: 'Standard 100 - tested for harmful substances' },
                            { emoji: '♻️', title: 'GRS', body: 'Global Recycled Standard for recycled content' },
                            { emoji: '🐑', title: 'RWS', body: 'Responsible Wool Standard for animal welfare' },
                            { emoji: '🌾', title: 'OCS', body: 'Organic Content Standard for organic materials' },
                            { emoji: '🇬🇧', title: 'British Wool', body: 'Traceable UK-sourced wool from British farms' }
                        ]
                    }
                ]
            },
            {
                key: 'cta',
                label: 'Call to action',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'Interested in Our Yarn Collection?' },
                    { key: 'body', label: 'Text', type: 'textarea', default: 'Contact us to discuss yarn options, request samples, or explore possibilities for your next project.' },
                    { key: 'buttonText', label: 'Button text', type: 'text', default: 'DISCUSS YARN OPTIONS' }
                ]
            }
        ]
    },

    // --------------------------------------------------------------- CONTACT
    {
        key: 'contact',
        label: 'Contact',
        path: '/contact',
        groups: [
            {
                key: 'hero',
                label: 'Hero',
                fields: [
                    { key: 'image', label: 'Background image', type: 'image', default: '/images/INTRO PAGE.jpg' },
                    { key: 'title', label: 'Page title', type: 'text', default: 'CONTACT US' }
                ]
            },
            {
                key: 'form',
                label: 'Contact form',
                fields: [
                    { key: 'heading', label: 'Heading', type: 'text', default: 'Get in touch to schedule an appointment' },
                    { key: 'nameLabel', label: 'Name field label', type: 'text', default: 'Name *' },
                    { key: 'emailLabel', label: 'Email field label', type: 'text', default: 'Email *' },
                    { key: 'phoneLabel', label: 'Phone field label', type: 'text', default: 'Phone' },
                    { key: 'companyLabel', label: 'Company field label', type: 'text', default: 'Company' },
                    { key: 'messageLabel', label: 'Message field label', type: 'text', default: 'Message *' },
                    { key: 'submitText', label: 'Submit button text', type: 'text', default: 'SEND MESSAGE' }
                ]
            },
            {
                key: 'details',
                label: 'Main contact details',
                description: 'The email address and phone number shown here come from Site-wide → Shared contact details.',
                fields: [
                    {
                        key: 'address',
                        label: 'Address (one line per row)',
                        type: 'lines',
                        default: ['Unit 5, Leyton Studios, 15 Argall Avenue', 'London, E10 7QE, United Kingdom']
                    }
                ]
            },
            {
                key: 'people',
                label: 'Individual contacts',
                fields: [
                    {
                        key: 'cards',
                        label: 'People',
                        type: 'list',
                        itemLabel: 'Person',
                        itemFields: [
                            { key: 'name', label: 'Name', type: 'text' },
                            { key: 'role', label: 'Role', type: 'text' },
                            { key: 'email', label: 'Email', type: 'text' },
                            { key: 'phone', label: 'Phone', type: 'text' },
                            { key: 'phoneLink', label: 'Phone (dial format)', type: 'text' }
                        ],
                        default: [
                            {
                                name: 'Aysen Bayram',
                                role: 'Co-Founder & Creative Director',
                                email: 'aysen@knitster-ldn.com',
                                phone: '+44 (0) 7946 534 504',
                                phoneLink: '+447946534504'
                            },
                            {
                                name: 'Emma McClelland',
                                role: 'Co-Founder & Design Director',
                                email: 'emma@knitster-ldn.com',
                                phone: '+44 (0) 7888 823 403',
                                phoneLink: '+447888823403'
                            }
                        ]
                    }
                ]
            },
            {
                key: 'visit',
                label: 'Visit our studio',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'VISIT OUR STUDIO' },
                    { key: 'intro', label: 'Intro text', type: 'textarea', default: 'We welcome visits to our London facility by appointment. See our production process firsthand, meet our team, and discuss your knitwear requirements in person.' },
                    { key: 'cardTitle', label: 'Card heading', type: 'text', default: 'Location' },
                    { key: 'addressTitle', label: 'Address first line (bold)', type: 'text', default: 'Unit 5, Leyton Studios' },
                    {
                        key: 'address',
                        label: 'Address remaining lines (one per row)',
                        type: 'lines',
                        default: ['15 Argall Avenue', 'London, E10 7QE', 'United Kingdom']
                    },
                    { key: 'transport', label: 'Transport note', type: 'textarea', default: "Easily accessible by public transport. We're located in East London, just a short distance from Leyton station." },
                    { key: 'mapButtonText', label: 'Map button text', type: 'text', default: 'VIEW ON MAP' },
                    { key: 'mapUrl', label: 'Map URL', type: 'url', default: 'https://maps.google.com/?q=Unit+5+Leyton+Studios+15+Argall+Avenue+London+E10+7QE' }
                ]
            },
            {
                key: 'hours',
                label: 'Working hours',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'WORKING HOURS' },
                    {
                        key: 'rows',
                        label: 'Opening hours',
                        type: 'list',
                        itemLabel: 'Row',
                        itemFields: [
                            { key: 'label', label: 'Days', type: 'text' },
                            { key: 'value', label: 'Hours', type: 'text' }
                        ],
                        default: [
                            { label: 'Monday - Friday', value: '9:00 AM - 6:00 PM' },
                            { label: 'Saturday', value: 'By Appointment Only' },
                            { label: 'Sunday', value: 'Closed' }
                        ]
                    },
                    { key: 'note', label: 'Note below the hours', type: 'textarea', default: 'Please email or call to schedule an appointment before visiting' }
                ]
            }
        ]
    },

    // -------------------------------------------------------- MODERN SLAVERY
    {
        key: 'modernSlavery',
        label: 'Modern Slavery Statement',
        path: '/modern-slavery',
        groups: [
            {
                key: 'hero',
                label: 'Hero',
                fields: [
                    { key: 'image', label: 'Background image', type: 'image', default: '/images/Our-Values.jpg' },
                    { key: 'title', label: 'Page title', type: 'text', default: 'MODERN SLAVERY STATEMENT' }
                ]
            },
            {
                key: 'commitment',
                label: 'Our commitment',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'OUR COMMITMENT' },
                    {
                        key: 'paragraphs',
                        label: 'Paragraphs (one per line)',
                        type: 'lines',
                        default: [
                            'Knitster LDN is committed to preventing modern slavery and human trafficking in all aspects of our business and supply chain. We take a zero-tolerance approach to any form of slavery, forced labour, or human trafficking.',
                            'As a London-based knitwear manufacturer, we understand our responsibility to conduct our business ethically and with integrity, ensuring that our operations and supply chain are free from exploitation.'
                        ]
                    }
                ]
            },
            {
                key: 'supplyChain',
                label: 'Supply chain',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'SUPPLY CHAIN' },
                    { key: 'intro', label: 'Intro text', type: 'textarea', default: 'Our supply chain encompasses our London factory and trusted manufacturing partners across multiple regions.' },
                    { key: 'partnersTitle', label: 'Partners card heading', type: 'text', default: 'Manufacturing Partners' },
                    {
                        key: 'partners',
                        label: 'Partner countries (one per line)',
                        type: 'lines',
                        default: ['United Kingdom', 'Turkey', 'Romania', 'Bulgaria', 'China', 'Italy']
                    },
                    { key: 'localTitle', label: 'Local sourcing heading', type: 'text', default: 'Local Sourcing' },
                    { key: 'localBody', label: 'Local sourcing text', type: 'textarea', default: 'When producing overseas we use local yarns where appropriate with the intention of simplifying the supply chain and minimising the footprint of the end product. We ensure full traceability and transparency throughout our supply chain.' }
                ]
            },
            {
                key: 'certifications',
                label: 'Certifications & standards',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'CERTIFICATIONS & STANDARDS' },
                    { key: 'intro', label: 'Intro text', type: 'textarea', default: 'We maintain rigorous standards and certifications to ensure ethical practices throughout our operations.' },
                    {
                        key: 'items',
                        label: 'Certifications',
                        type: 'list',
                        itemLabel: 'Certification',
                        itemFields: [
                            { key: 'title', label: 'Name', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            { title: 'SEDEX 4 Pillar', body: 'We are committed to the SEDEX 4 Pillar ethical audit framework covering labour standards, health and safety, environment, and business ethics.' },
                            { title: 'Self Audits', body: 'Where formal certifications are unavailable for partner facilities, we conduct thorough self-audits to ensure compliance with our ethical standards.' },
                            { title: 'ISO 9001', body: 'Quality management system certification ensuring consistent quality and continuous improvement across our operations.' },
                            { title: 'Scope Certificate', body: 'Transaction certificates ensuring traceability and verification of certified materials throughout our supply chain.' }
                        ]
                    }
                ]
            },
            {
                key: 'policies',
                label: 'Our policies',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'OUR POLICIES' },
                    {
                        key: 'items',
                        label: 'Policy bullets (one per line)',
                        type: 'lines',
                        default: [
                            'We verify the age of all workers and do not employ anyone under the legal working age',
                            'All employment is voluntary - we do not use forced, bonded or compulsory labour',
                            'Workers are free to leave employment after reasonable notice',
                            'We pay fair wages that meet or exceed legal minimum requirements',
                            'Working hours comply with national laws and industry standards',
                            'We provide safe and healthy working conditions for all employees',
                            "We do not retain workers' identity documents or require deposits"
                        ]
                    }
                ]
            },
            {
                key: 'dueDiligence',
                label: 'Due diligence',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'DUE DILIGENCE' },
                    {
                        key: 'items',
                        label: 'Cards',
                        type: 'list',
                        itemLabel: 'Card',
                        itemFields: [
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            { title: 'Supplier Assessment', body: 'All new suppliers undergo thorough assessment including site visits, documentation review, and evaluation against our ethical standards.' },
                            { title: 'Ongoing Monitoring', body: 'We conduct regular audits and maintain close relationships with our suppliers to ensure continued compliance with our policies.' },
                            { title: 'Reporting Mechanisms', body: 'We encourage workers and suppliers to report concerns through our confidential reporting channels without fear of retaliation.' },
                            { title: 'Training', body: 'Our staff receive training on identifying and responding to modern slavery risks in our business and supply chain.' }
                        ]
                    }
                ]
            },
            {
                key: 'cta',
                label: 'Call to action',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'Questions About Our Ethical Practices?' },
                    { key: 'body', label: 'Text', type: 'textarea', default: 'We are committed to transparency and welcome any questions about our modern slavery policies and practices.' },
                    { key: 'buttonText', label: 'Button text', type: 'text', default: 'CONTACT US' }
                ]
            }
        ]
    },

    // -------------------------------------------------- ENVIRONMENTAL POLICY
    {
        key: 'environmentalPolicy',
        label: 'Environmental Policy',
        path: '/environmental-policy',
        groups: [
            {
                key: 'hero',
                label: 'Hero',
                fields: [
                    { key: 'image', label: 'Background image', type: 'image', default: '/images/Our-Values.jpg' },
                    { key: 'title', label: 'Page title', type: 'text', default: 'ENVIRONMENTAL POLICY' }
                ]
            },
            {
                key: 'commitment',
                label: 'Our commitment',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'OUR COMMITMENT' },
                    { key: 'body', label: 'Text', type: 'textarea', default: 'At Knitster LDN, environmental responsibility is at the heart of everything we do. We are committed to minimising our environmental impact while producing exceptional quality knitwear. Our approach focuses on sustainable materials, efficient production processes, and responsible waste management.' }
                ]
            },
            {
                key: 'freight',
                label: 'Freight and shipping',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'FREIGHT AND SHIPPING' },
                    {
                        key: 'items',
                        label: 'Bullets (one per line)',
                        type: 'lines',
                        default: [
                            'Avoiding airfreight where possible for overseas production',
                            'Minimising the number of shipments to our overseas factories by knitting down and developing stitches in London',
                            'Producing in local yarns to shorten the supply chain'
                        ]
                    }
                ]
            },
            {
                key: 'waste',
                label: 'Waste reduction',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'WASTE REDUCTION' },
                    {
                        key: 'items',
                        label: 'Bullets (one per line)',
                        type: 'lines',
                        default: [
                            'We only use cut and sew techniques where it is absolutely essential',
                            'The vast majority of our production is fully fashioned, thus we produce zero waste garments',
                            'We sell end cones of yarn in our dead-stock yarn shop, Knitstore LDN',
                            'We backwind all broken panels where possible',
                            "Any broken panels that can't be backwound are repurposed using learnings gained when we completed the Innovate UK pilot",
                            'We are in the process of phasing out detergent and softeners in the washing process and are migrating to the Iafil Washingball technology'
                        ]
                    }
                ]
            },
            {
                key: 'materials',
                label: 'Sustainable materials',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'SUSTAINABLE MATERIALS' },
                    {
                        key: 'cards',
                        label: 'Material cards',
                        type: 'list',
                        itemLabel: 'Material',
                        itemFields: [
                            { key: 'image', label: 'Icon', type: 'image' },
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            {
                                image: '/images/British-Wool.png',
                                title: 'British Wool',
                                body: 'We champion British wool from local farms, celebrating native breeds and supporting UK agriculture.'
                            },
                            {
                                image: '/images/Natural-Fibres.png',
                                title: 'Natural Fibres',
                                body: 'Prioritising natural, biodegradable fibres that have minimal environmental impact at end of life.'
                            },
                            {
                                image: '/images/Recycle-Yarn.png',
                                title: 'Recycled Yarns',
                                body: 'Working with recycled yarns that give new life to post-consumer and post-industrial textile waste.'
                            }
                        ]
                    }
                ]
            },
            {
                key: 'energy',
                label: 'Energy and resources',
                fields: [
                    { key: 'title', label: 'Section heading', type: 'text', default: 'ENERGY AND RESOURCES' },
                    {
                        key: 'cards',
                        label: 'Cards',
                        type: 'list',
                        itemLabel: 'Card',
                        itemFields: [
                            { key: 'title', label: 'Title', type: 'text' },
                            { key: 'body', label: 'Description', type: 'textarea' }
                        ],
                        default: [
                            { title: 'Energy Efficiency', body: 'We continuously monitor and work to reduce our energy consumption across our London facility, investing in efficient machinery and sustainable practices.' },
                            { title: 'Water Conservation', body: 'Our Iafil Washingball technology significantly improves the quality of our waste water as it eliminates the need for chemicals in the washing process.' }
                        ]
                    }
                ]
            },
            {
                key: 'improvement',
                label: 'Continuous improvement',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'CONTINUOUS IMPROVEMENT' },
                    {
                        key: 'paragraphs',
                        label: 'Paragraphs (one per line)',
                        type: 'lines',
                        default: [
                            'We are committed to continuously improving our environmental performance. We regularly review our practices, set new targets, and explore innovative solutions to reduce our environmental footprint while maintaining the highest standards of quality and craftsmanship.',
                            'Our participation in initiatives like the Innovate UK pilot demonstrates our commitment to innovation in sustainable manufacturing practices.'
                        ]
                    }
                ]
            },
            {
                key: 'cta',
                label: 'Call to action',
                fields: [
                    { key: 'title', label: 'Heading', type: 'text', default: 'Questions About Our Environmental Practices?' },
                    { key: 'body', label: 'Text', type: 'textarea', default: "We're happy to discuss our environmental commitments and sustainable practices in more detail." },
                    { key: 'buttonText', label: 'Button text', type: 'text', default: 'CONTACT US' }
                ]
            }
        ]
    }
];

/** Build the flat defaults object: { pageKey: { groupKey: { fieldKey: value } } } */
const buildDefaults = () => {
    const defaults = {};
    CONTENT_SCHEMA.forEach(page => {
        defaults[page.key] = {};
        page.groups.forEach(group => {
            defaults[page.key][group.key] = {};
            group.fields.forEach(field => {
                defaults[page.key][group.key][field.key] = field.default;
            });
        });
    });
    return defaults;
};

export const DEFAULT_CONTENT = buildDefaults();

/** Look up a field definition by "pageKey.groupKey.fieldKey" style coordinates. */
export const findField = (pageKey, groupKey, fieldKey) => {
    const page = CONTENT_SCHEMA.find(p => p.key === pageKey);
    const group = page && page.groups.find(g => g.key === groupKey);
    return group ? group.fields.find(f => f.key === fieldKey) : undefined;
};

/** Deep clone that is safe for the plain JSON values used in the schema. */
export const cloneValue = (value) => JSON.parse(JSON.stringify(value));
